import React, { useState } from "react";
import { Flag, X, Loader2, ShieldAlert, Bug, Cog, FileWarning, FileX, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASONS = [
  { id: "broken",        label: "Broken / Dead torrent",     desc: "0 seeders or won't connect",              Icon: Cog,                  accent: "#FFD700" },
  { id: "malware",       label: "Malware / Virus",            desc: "Files trigger antivirus alerts",          Icon: ShieldAlert,          accent: "#EF4444" },
  { id: "fake",          label: "Fake / Decoy",               desc: "File doesn't match the title or size",    Icon: FileX,                accent: "#FF6B35" },
  { id: "wrong_content", label: "Wrong content",              desc: "Torrent is a different movie/show",       Icon: FileWarning,          accent: "#6D4AFF" },
  { id: "low_quality",   label: "Bad quality / Mislabelled",  desc: "Cam-rip listed as 1080p, audio sync etc", Icon: Bug,                  accent: "#22C77A" },
  { id: "other",         label: "Other",                      desc: "Explain below",                           Icon: MessageSquareWarning, accent: "#FF2A6D" },
];

export default function ReportTorrentModal({ row, tmdbId, mediaType, onClose }) {
  const [reason, setReason] = useState("broken");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!row?.info_hash) {
      toast.error("This row has no info-hash to report");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/torrents/${row.info_hash}/report`, {
        reason,
        note: note.trim() || null,
        name: row.name || null,
        tmdb_id: tmdbId ?? null,
        media_type: mediaType ?? null,
      });
      if (data?.deduped) {
        toast.success("You've already reported this — thanks!");
      } else {
        toast.success("Report submitted to mods. Thank you!");
      }
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="report-modal-backdrop"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="report-modal"
        className="w-full max-w-lg bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-3xl shadow-2xl overflow-hidden relative"
      >
        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-30 blur-3xl pointer-events-none" style={{ background: "#FF2A6D" }} />

        <div className="relative px-6 py-4 border-b border-[var(--tt-border)] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 flex items-center justify-center">
            <Flag className="w-4 h-4 text-[var(--tt-primary)]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-extrabold tracking-tight">Report this torrent</h3>
            <div className="text-[11px] text-[var(--tt-text-muted)] font-mono-tt truncate">
              {row?.name || row?.info_hash}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="report-modal-close"
            className="p-2 rounded-full hover:bg-white/5 text-[var(--tt-text-muted)] hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative px-6 py-5 max-h-[60vh] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-text-muted)] mb-3">
            What&apos;s wrong with it?
          </div>
          <div className="space-y-2">
            {REASONS.map((r) => {
              const selected = reason === r.id;
              return (
                <label
                  key={r.id}
                  data-testid={`report-reason-${r.id}`}
                  className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-all ${
                    selected
                      ? "border-[var(--tt-primary)]/60 bg-[var(--tt-primary)]/8"
                      : "border-[var(--tt-border)] bg-black/20 hover:border-[var(--tt-primary)]/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.id}
                    checked={selected}
                    onChange={() => setReason(r.id)}
                    className="sr-only"
                  />
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${r.accent}33, transparent 60%), #0b0b0b`,
                      boxShadow: selected ? `inset 0 0 0 1px ${r.accent}88` : `inset 0 0 0 1px ${r.accent}33`,
                    }}
                  >
                    <r.Icon className="w-4 h-4" style={{ color: r.accent }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{r.label}</div>
                    <div className="text-[11px] text-[var(--tt-text-muted)] leading-snug">{r.desc}</div>
                  </div>
                  <span
                    className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 transition-colors ${
                      selected ? "border-[var(--tt-primary)]" : "border-white/20"
                    }`}
                  >
                    {selected && <span className="block w-1.5 h-1.5 rounded-full bg-[var(--tt-primary)] m-[3px]" />}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4">
            <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-text-muted)] mb-2">
              Optional note <span className="text-[var(--tt-text-faint)]">(500 chars)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Add context — file names, antivirus result, anything that helps mods verify."
              data-testid="report-note-input"
              className="w-full bg-black/30 border border-[var(--tt-border)] rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-[var(--tt-primary)] resize-none"
            />
            <div className="text-[10px] text-right text-[var(--tt-text-faint)] mt-1 font-mono-tt">
              {note.length}/500
            </div>
          </div>
        </div>

        <div className="relative px-6 py-4 border-t border-[var(--tt-border)] flex items-center justify-between gap-3">
          <span className="text-[11px] text-[var(--tt-text-faint)] font-mono-tt">
            Anonymous · one report per day per IP
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="report-cancel"
              className="text-xs font-semibold px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-white/80"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="report-submit"
              className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
              Submit report
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
